import './Home.css';

import { useState, useEffect, CSSProperties, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocateFixed, X } from 'lucide-react';

import Map from '../../components/map/Map';
import ChargerSitesList from '../../components/chargerSiteCardList/ChargerSiteCardList';
import RechargeCardList from '../../components/rechargeCardList/RechargeCardList';
import RechargeCard from '../../components/rechargeCard/RechargeCard';
import SwipeToRefresh from '../../components/SwipeToRefresh/SwipeToRefresh';

import imageMap from '../../assets/fi-sr-map.svg';
import qrCode from '../../assets/qr_code.svg';
import search from '../../assets/search.svg';

import { ReactComponent as LoaderSecondary } from '../../assets/SpinnerBlack.svg';

import { useSocketContext } from '../../websocket/websocketProvider';
import { useToast } from '../../context/ToastModalContext';
import { useAuth } from '../../context/AuthContext';
import {
  useLocationContext,
  saveView,
  loadSavedView,
} from '../../context/LocationContext';
import { useBrandContext } from '../../context/BrandContext';

import { useMapViewStore } from '../../hooks/useMapViewStore';
import { useRecharge } from '../../hooks/useRecharge';
import { useAddressSearch } from '../../hooks/useAddressSearch';

import {
  ChargerSiteStatus,
  getChargerSites,
  getChargerSitesStatus,
} from '../../services/chargerSitesServices';
import { useHeaderContext } from '../../context/HeaderContext';
import Modal from '../../components/modal/Modal';
import { isWebView, isAppleDevice } from '../../utils/detectWebContainer';
import { getBuildNumber } from '../../utils/getBuildNumber';

const appStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const noSitesStyle: CSSProperties = {
  fontSize: '14px',
  width: '100%',
  textAlign: 'center',
  marginTop: '10px',
};

// Atualização automática do status dos pins: só os sites mais próximos do ponto de pesquisa
const SITES_STATUS_REFRESH_INTERVAL_MS = 30 * 1000;
const SITES_STATUS_REFRESH_LIMIT = 50;

const isSameSiteStatus = (
  current: Partial<ChargerSiteStatus> | undefined,
  next: ChargerSiteStatus
) =>
  !!current &&
  current.available === next.available &&
  current.inUse === next.inUse &&
  current.offLine === next.offLine &&
  current.total === next.total &&
  current.onHold === next.onHold;

const Home = () => {
  const [chargerSites, setChargerSites] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [error, setError] = useState('');
  const [showMap, setShowMap] = useState<boolean>(() => {
    const saved = localStorage.getItem('showMap');
    return saved ? JSON.parse(saved) : false;
  });
  const [isActiveRechargesModalOpen, setIsActiveRechargesModalOpen] =
    useState<boolean>(false);
  const [isSiteDetailModalOpen, setIsSiteDetailModalOpen] =
    useState<boolean>(false);
  const isRefreshingSitesStatusRef = useRef(false);
  // Muda a cada busca: reinicia o timer do refresh e invalida refresh em andamento
  const sitesSearchVersionRef = useRef(0);
  const [sitesSearchVersion, setSitesSearchVersion] = useState(0);
  const { messages } = useSocketContext();
  const { showToast } = useToast();
  const authContext = useAuth();
  const locationContext = useLocationContext();
  const brandContext = useBrandContext();
  const { setHeaderConfig } = useHeaderContext();
  const {
    isCsDetailActive,
    setIsCsDetailActive,
    triggerAddressSearch,
    setTriggerAddressSearch,
    hasNewSearch,
    setHasNewSearch,
    map,
  } = useMapViewStore();
  const navigate = useNavigate();
  const webView = isWebView();
  const appleDevice = isAppleDevice();
  const buildNumber = getBuildNumber(
    webView ? (authContext.appVersion as string) : null
  );
  const bottomSpacingVersion = 163;

  // hooks customizados
  const { recharge, fetchRecharge, loadingRecharge, errorRecharge } =
    useRecharge(authContext.token!);
  const { searchInput, setSearchInput, addressesList, isSearchCompleted } =
    useAddressSearch();

  // alternar mapa/lista
  const toggleView = () => {
    setTriggerAddressSearch(!triggerAddressSearch);
    setShowMap((prev) => {
      localStorage.setItem('showMap', JSON.stringify(!prev));
      return !prev;
    });
  };

  // abrir câmera
  const openCam = () => {
    if (authContext.user) {
      navigate(`/${brandContext.brandName}/qrcode`);
    } else {
      showToast({
        title: `Faça Login`,
        messageModal: `Faça login para usar QR code`,
        textEnterButton: 'Ir para login',
        allowButtonClosed: true,
        handleEnterButton: () => navigate(`/${brandContext.brandName}/login`),
        closeButtonText: 'Fechar',
      });
    }
  };

  // selecionar endereço
  const setNewAddress = useCallback(
    (latLong: any, formattedAddress: string) => {
      setSearchInput(formattedAddress);
      locationContext.setSearchLocation({
        lat: latLong.latitude,
        lng: latLong.longitude,
      });
      setHasNewSearch(true);
    },
    [locationContext, setSearchInput]
  );

  // buscar sites
  const fetchChargerSites = useCallback(
    async (lat: number, lng: number, dist: number) => {
      if (!locationContext.searchLocation) return;
      setLoading(true);
      sitesSearchVersionRef.current++;
      try {
        if (locationContext.isGpsLocation === null) return;
        const sites = await getChargerSites(lat, lng, locationContext, dist);
        // A busca já traz o status atual: refresh que estava em andamento é descartado
        // e o próximo só roda 30 s depois dela
        sitesSearchVersionRef.current++;
        setChargerSites(sites);
        setSitesSearchVersion(sitesSearchVersionRef.current);
        setError('');
      } catch (error: any) {
        // Só loga se não for erro de rede ou página oculta
        if (error?.message !== 'Network Error' && error?.message !== 'Page not visible') {
          console.error('Erro ao buscar sites de carregamento', error);
          setError(
            'Erro ao buscar sites de carregamento. Por favor, tente novamente'
          );
        }
      }
      setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationContext.isGpsLocation, locationContext.userLocation]
  );

  // Refresh para quem fica parado no mapa (a busca em si já traz o status atual).
  // Troca só o chargerSiteStatus de quem mudou: site sem mudança mantém o mesmo objeto e, sem
  // nenhuma mudança, o estado nem é alterado — os Marker (key = csId) não são recriados, o
  // mapa só troca o ícone de quem mudou de cor. Falha é silenciosa; o próximo ciclo tenta de novo.
  const refreshSitesStatus = async () => {
    const reference = locationContext.searchLocation;
    if (
      document.hidden ||
      loading ||
      isRefreshingSitesStatusRef.current ||
      !reference ||
      chargerSites.length === 0
    )
      return;

    const siteIds = chargerSites
      .map((site) => ({
        csId: site.csId,
        distance: locationContext.getDistance(
          {
            from: reference,
            to: {
              lat: site.csLocation.coordinates[1],
              lng: site.csLocation.coordinates[0],
            },
          },
          'm',
          0
        ),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, SITES_STATUS_REFRESH_LIMIT)
      .map((site) => site.csId);

    const searchVersion = sitesSearchVersionRef.current;
    isRefreshingSitesStatusRef.current = true;
    try {
      const response = await getChargerSitesStatus(siteIds);
      if (searchVersion !== sitesSearchVersionRef.current) return;
      const statusBySite: Record<string, ChargerSiteStatus> = {};
      Object.entries(response).forEach(([csId, status]) => {
        statusBySite[csId.toLowerCase()] = status;
      });

      setChargerSites((current) => {
        let hasChanges = false;
        const next = current.map((site) => {
          const status = statusBySite[String(site.csId).toLowerCase()];
          if (!status || isSameSiteStatus(site.chargerSiteStatus, status))
            return site;
          hasChanges = true;
          return {
            ...site,
            chargerSiteStatus: { ...site.chargerSiteStatus, ...status },
          };
        });
        return hasChanges ? next : current;
      });
    } catch {
      // silencioso: mantém os pins como estão
    } finally {
      isRefreshingSitesStatusRef.current = false;
    }
  };

  // O timer chama sempre a versão mais recente (sites e ponto de pesquisa atuais)
  const refreshSitesStatusRef = useRef(refreshSitesStatus);
  refreshSitesStatusRef.current = refreshSitesStatus;

  // Só com o mapa aberto; reinicia a cada busca
  useEffect(() => {
    if (!showMap) return;
    const intervalId = setInterval(
      () => refreshSitesStatusRef.current(),
      SITES_STATUS_REFRESH_INTERVAL_MS
    );
    // Aba em segundo plano pula os ciclos; ao voltar, atualiza na hora
    const handleVisibilityChange = () => {
      if (!document.hidden) refreshSitesStatusRef.current();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showMap, sitesSearchVersion]);

  const centerMap = () => {
    if (!map || !locationContext.userLocation) return;
    map.panTo(locationContext.userLocation);
    setTimeout(() => {
      map.setZoom(15);
      const center: any = map.getCenter();
      if (!center) return;
      const bounds: any = map.getBounds();
      saveView({
        center: { lat: center.lat(), lng: center.lng() },
        bounds,
        zoom: 15,
      });
    }, 500);
  };

  const resetSearch = () => {
    if (!map || !locationContext.userLocation) return;
    locationContext.setSearchLocation(locationContext.userLocation);
    setSearchInput('');
    centerMap();
  };

  // inicialização
  useEffect(() => {
    locationContext.refreshCurrentPosition();
    setIsCsDetailActive(false);
  }, []);

  // após login
  useEffect(() => {
    setTimeout(() => {
      if (!map) return;
      centerMap();
    }, 500);
  }, [authContext.user]);

  // websocket → atualizar recargas
  useEffect(() => {
    const socketMessagesJson = messages?.map((jsonString) =>
      JSON.parse(jsonString)
    );
    socketMessagesJson.forEach((msg) => {
      if (
        msg.eventType === 'StartTransactionIntegrationEvent' ||
        msg.eventType === 'StopTransactionIntegrationEvent'
      ) {
        setTimeout(fetchRecharge, 1000);
      }
    });
  }, [messages, fetchRecharge]);

  // atualizar sites quando localização mudar
  useEffect(() => {
    if (locationContext.searchLocation)
      fetchChargerSites(
        locationContext.searchLocation.lat,
        locationContext.searchLocation.lng,
        locationContext.searchRadius
      );
  }, [
    fetchChargerSites,
    hasNewSearch,
    locationContext.searchLocation,
    locationContext.searchRadius,
    locationContext.setSearchRadius,
  ]);

  useEffect(() => {
    setHeaderConfig({
      headerText: '',
      showLogo: true,
      showActionButtons: false,
      hasLoginInfo: true,
    });
    return () => setHeaderConfig({});
  }, [navigate, setHeaderConfig]);

  useEffect(() => {
    if (!map) return;
    const savedView: any = loadSavedView();
    if (!savedView?.center) {
      centerMap();
    }
  }, [map]);

  return (
    <div style={appStyle}>
      <div>
        {/* Busca endereço */}
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar endereço"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setIsSearching(true);
            }}
          />
          {searchInput.length > 0 ? (
            <X
              style={{ marginTop: -2, cursor: 'pointer' }}
              className="search-icon"
              width={26}
              height={26}
              color="#979797"
              onClick={resetSearch}
            />
          ) : (
            <img
              className="search-icon"
              src={search}
              alt="Lupa"
            />
          )}
          <div
            className={`search-reset-container${showMap ? ' map' : ''}`}
            onClick={resetSearch}
          >
            <LocateFixed
              className="search-reset"
              width={26}
              height={26}
              color="#979797"
            />
          </div>
          {searchInput && isSearchCompleted && isSearching && (
            <ul className="suggestions-addresses">
              {addressesList.length > 0 ? (
                addressesList.map((item: any) => (
                  <li
                    onClick={() => {
                      setNewAddress(item.coordinates, item.formattedAddress);
                      setIsSearching(false);
                    }}
                    className="item-address"
                    key={item.placeId}
                  >
                    {item.formattedAddress}
                  </li>
                ))
              ) : (
                <li className="item-address">Endereço não encontrado</li>
              )}
            </ul>
          )}
        </div>

        {/* Recargas */}
        {!isActiveRechargesModalOpen && (
          <div
            className="charge-list"
            onClick={() => {
              setTimeout(
                () => {
                  setIsActiveRechargesModalOpen(true);
                },
                isSiteDetailModalOpen ? 250 : 0
              );
              setIsSiteDetailModalOpen(false);
            }}
          >
            <RechargeCardList recharges={recharge} />
            {errorRecharge && (
              <div className="error-list-home">{errorRecharge}</div>
            )}
            {loadingRecharge && <LoaderSecondary />}
          </div>
        )}
      </div>
      <Map
        chargerSites={chargerSites}
        isLoadingPins={loading}
        style={showMap ? {} : { display: 'none' }}
        showMap={showMap}
        isSiteDetailModalOpen={isSiteDetailModalOpen}
        setIsSiteDetailModalOpen={setIsSiteDetailModalOpen}
        isActiveRechargesModalOpen={isActiveRechargesModalOpen}
        setIsActiveRechargesModalOpen={setIsActiveRechargesModalOpen}
      />
      <div style={!showMap ? {} : { display: 'none' }}>
        <SwipeToRefresh
          onSwipeDown={() => {
            if (locationContext.searchLocation)
              fetchChargerSites(
                locationContext.searchLocation.lat,
                locationContext.searchLocation.lng,
                locationContext.searchRadius
              );

            if (authContext.token) {
              fetchRecharge();
            }
          }}
        >
          <div>
            {/* Lista de carregadores */}
            <div className="title-list">
              {locationContext.userLocation === locationContext.searchLocation
                ? 'Próximo a você'
                : ''}
            </div>
            {!loading && chargerSites.length > 0 ? (
              <ChargerSitesList chargerSites={chargerSites} />
            ) : loading ? (
              <LoaderSecondary />
            ) : (
              <p style={noSitesStyle}>
                Não há sites de carregamento disponíveis
              </p>
            )}
            {error && !loading && (
              <div className="error-list-home">{error}</div>
            )}
          </div>
        </SwipeToRefresh>
      </div>

      {/* Rodapé */}
      <div
        className="footer_toggle-buttons"
        style={
          buildNumber >= bottomSpacingVersion
            ? webView && appleDevice
              ? { bottom: 40 }
              : webView
              ? { bottom: 70 }
              : {}
            : {}
        }
      >
        <button
          className="map-button"
          onClick={toggleView}
        >
          <span>{showMap ? 'Lista' : 'Mapa'}</span>
          <img
            src={imageMap}
            alt="ImageMap"
          />
        </button>
        <button
          className="qrcode"
          onClick={openCam}
        >
          <span>QR Code</span>
          <img
            src={qrCode}
            alt="QrCode"
          />
        </button>
      </div>

      <Modal
        allowButtonClosed={false}
        isOpen={isActiveRechargesModalOpen}
        onClose={() => {
          setIsActiveRechargesModalOpen(false);
        }}
        textEnterButton=""
        handleEnterButton={() => {}}
        isLoadingEnterButton={false}
        styleEnterButton={{ backgroundColor: `${brandContext.brandBgColor}` }}
        disableEnterButton={true}
        modalBackdrop={false}
        header={false}
      >
        <div
          className="group-list"
          style={{ marginTop: 10 }}
        >
          {recharge.map((recharge: any, index: number) => (
            <RechargeCard
              key={index}
              recharge={recharge}
            />
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Home;
